(() => {
  if (!customElements.get('fn-predictive-search')) customElements.define('fn-predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.fn-search-results');
      this.discovery = this.querySelector('.fn-search-discovery');
      this.status = this.querySelector('.fn-search-status');
      this.clear = this.querySelector('[type="reset"]');
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', () => this.schedule(), { signal });
      this.querySelector('form').addEventListener('reset', () => {
        this.input.value = '';
        this.schedule();
        this.input.focus();
      }, { signal });
      this.querySelectorAll('[data-search-query]').forEach((button) => button.addEventListener('click', () => {
        this.input.value = button.dataset.searchQuery;
        this.schedule();
        this.input.focus();
      }, { signal }));
    }
    schedule() {
      clearTimeout(this.timer);
      this.request?.abort();
      const query = this.input.value.trim();
      this.clear.hidden = !query;
      this.input.setAttribute('aria-expanded', 'false');
      this.results.hidden = true;
      this.results.replaceChildren();
      this.discovery.hidden = false;
      this.status.textContent = '';
      if (!query) return;
      this.status.textContent = 'Searching…';
      this.timer = setTimeout(() => this.search(query), 200);
    }
    async search(query) {
      this.request = new AbortController();
      try {
        const url = new URL(this.dataset.searchUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,article,page');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('section_id', 'fn-predictive-search');
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || this.input.value.trim() !== query) return;
        const section = parsed.querySelector('#shopify-section-fn-predictive-search');
        if (!section) throw new Error('Missing search results');
        this.results.replaceChildren(...section.childNodes);
        this.results.hidden = false;
        this.discovery.hidden = true;
        this.input.setAttribute('aria-expanded', 'true');
        this.status.textContent = `${this.results.querySelectorAll('li').length} suggestions for ${query}`;
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected && this.input.value.trim() === query) {
          this.status.textContent = 'Suggestions are unavailable. Press Enter to search all results.';
        }
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.request?.abort(); clearTimeout(this.timer); }
  });

  document.addEventListener('click', (event) => {
    const searchOpener = event.target.closest('[data-drawer-open="search"]');
    if (searchOpener) {
      const header = document.querySelector('.shopify-section-header');
      const top = Math.max(0, Math.min(innerHeight, header?.getBoundingClientRect().bottom || 0));
      document.querySelector('#SearchDrawer')?.style.setProperty('--fn-search-top', `${top}px`);
    }
    const playMedia = event.target.closest('[data-native-media-play]');
    if (playMedia) {
      const container = playMedia.closest('.product-media-container');
      const template = container.querySelector('[data-native-media-template]');
      if (template && !container.querySelector('[data-native-media-player]')) {
        const player = document.createElement('div');
        player.dataset.nativeMediaPlayer = '';
        player.append(template.content.cloneNode(true));
        container.append(player);
        playMedia.hidden = true;
        player.querySelector('video,iframe,model-viewer')?.focus();
      }
    }
    const galleryButton = event.target.closest('.replica-product-gallery .product__media-zoom-lightbox');
    if (galleryButton) {
      const gallery = galleryButton.closest('.replica-product-gallery');
      const owner = galleryButton.closest('product-info');
      const dialog = owner?.querySelector('[data-media-dialog]');
      if (!dialog) return;
      const images = [...gallery.querySelectorAll('.product__media-zoom-lightbox')].map((button) => ({ url: button.dataset.productThumb, alt: button.dataset.alt || '' }));
      dialog.fnImages = images;
      dialog.fnIndex = Math.max(0, [...gallery.querySelectorAll('.product__media-zoom-lightbox')].indexOf(galleryButton));
      dialog.fnOpener = galleryButton;
      renderMedia(dialog);
      dialog.showModal();
      document.body.classList.add('fn-media-open');
    }
    const mediaClose = event.target.closest('[data-media-close]');
    if (mediaClose) mediaClose.closest('dialog').close();
    const mediaDirection = event.target.closest('[data-media-direction]');
    if (mediaDirection) {
      const dialog = mediaDirection.closest('dialog');
      dialog.fnIndex = (dialog.fnIndex + Number(mediaDirection.dataset.mediaDirection) + dialog.fnImages.length) % dialog.fnImages.length;
      renderMedia(dialog);
    }
    const cardDirection = event.target.closest('[data-card-direction]');
    if (cardDirection) {
      event.preventDefault();
      const card = cardDirection.closest('.sb-product-card');
      const images = JSON.parse(card.querySelector('[data-card-images]').textContent);
      const index = Math.max(0, Math.min(images.length - 1, Number(card.dataset.imageIndex || 0) + Number(cardDirection.dataset.cardDirection)));
      card.dataset.imageIndex = String(index);
      const image = card.querySelector('.sb-product-card__media .sb-product-card__image');
      image.removeAttribute('srcset');
      image.src = images[index].url;
      image.alt = images[index].alt;
      card.querySelector('[data-card-direction="-1"]').disabled = index === 0;
      card.querySelector('[data-card-direction="1"]').disabled = index === images.length - 1;
    }
  });
  function renderMedia(dialog) {
    const media = dialog.fnImages[dialog.fnIndex];
    const image = dialog.querySelector('img');
    image.src = media.url;
    image.alt = media.alt;
    dialog.querySelector('[data-media-count]').textContent = `${dialog.fnIndex + 1} / ${dialog.fnImages.length}`;
  }
  const initializeMedia = (root = document) => {
    root.querySelectorAll('[data-media-dialog]').forEach((dialog) => {
      dialog.fnController?.abort();
      dialog.fnController = new AbortController();
      const { signal } = dialog.fnController;
      dialog.addEventListener('close', () => {
        document.body.classList.remove('fn-media-open');
        dialog.fnOpener?.focus();
      }, { signal });
      dialog.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        dialog.fnIndex = (dialog.fnIndex + (event.key === 'ArrowRight' ? 1 : -1) + dialog.fnImages.length) % dialog.fnImages.length;
        renderMedia(dialog);
      }, { signal });
    });
  };
  initializeMedia();
  document.addEventListener('shopify:section:load', (event) => initializeMedia(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-media-dialog]').forEach((dialog) => {
    if (dialog.open) dialog.close();
    dialog.fnController?.abort();
  }));
})();
